import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import api from "../../../utils/axios";
import moment from "moment";
import "moment/locale/th";
import Swal from "sweetalert2";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import Pagination from "../../components/Pagination";
import DatePicker from "../../components/DatePicker";
import CallModal from "../../components/Modal/CallModal";
import { Icon } from "@iconify/react";

export default function SaleCallDetail() {
  const { Emp_Id } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const dateParam = queryParams.get("date");

  const [data, setData] = useState([]);
  const [empName, setEmpName] = useState("");
  const [selectedDate, setSelectedDate] = useState(dateParam || "");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // SORT STATE
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  // MODAL
  const [showCallModal, setShowCallModal] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);

  const breadcrumbs = [
    { label: "ขาย", link: "/sale" },
    { label: "ตรวจสอบฝ่ายขาย", link: "/sale-report" },
    { label: "รายละเอียดการติดต่อ" },
  ];

  // โหลดข้อมูล
  const fetchData = async (date = "") => {
    setLoading(true);
    try {
      const url = date
        ? `/get-sale-call-detail?Emp_Id=${Emp_Id}&date=${date}`
        : `/get-sale-call-detail?Emp_Id=${Emp_Id}`;

      const res = await api.get(url);

      if (res.data?.status === "OK") {
        setData(res.data.result || []);
        setEmpName(res.data.empName || "");
      } else {
        Swal.fire("ผิดพลาด", res.data?.message || "โหลดข้อมูลล้มเหลว", "error");
      }
    } catch (err) {
      Swal.fire("ผิดพลาด", err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initialDate = dateParam || new Date().toISOString().split("T")[0];
    setSelectedDate(initialDate);
    fetchData(initialDate);
  }, [Emp_Id]);

  // ESC back
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        localStorage.setItem("saleReportDate", selectedDate);
        localStorage.setItem("saleReportSearch", searchText);
        localStorage.setItem("saleReportTab", "call");
        window.location.href = `/sale-report?date=${selectedDate}`;
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [selectedDate, searchText]);

  // Format -------------------------------------------------------------------

  const formatPhone = (tel) => {
    if (!tel) return "-";
    const digits = tel.replace(/\D/g, "").slice(0, 10);
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const formatThaiDateTimeFullCall = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const d = moment.parseZone(dateStr);
      const buddhistYear = d.year() + 543;
      const dateText = d.format(`DD/MMM/${buddhistYear}`);
      const timeText = d.format("HH:mm");
      return `${dateText} ${timeText}`;
    } catch {
      return "-";
    }
  };

  const formatThaiDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const d = moment.parseZone(dateStr);
      const buddhistYear = d.year() + 543;
      return d.format(`DD/MMM/${buddhistYear}`);
    } catch {
      return "-";
    }
  };

  const formatTime = (t) => {
    if (!t) return "";
    try {
      return moment.parseZone(t).format("HH:mm");
    } catch {
      return "";
    }
  };

  // FILTER (search ทุกช่อง)
  const filteredData = data.filter((d) => {
    const text = searchText.toLowerCase();

    return (
      (d.AR_Name || "").toLowerCase().includes(text) ||
      (d.AR_LastName || "").toLowerCase().includes(text) ||
      (d.AR_Tel || "").toLowerCase().includes(text) ||
      (d.Contact_Name || "").toLowerCase().includes(text) ||
      (d.Contact_Position || "").toLowerCase().includes(text) ||
      (d.Call_Result || "").toLowerCase().includes(text)
    );
  });

  // ========== SORT ICON ==========
  const SortIcon = ({ column }) => (
    <span style={{ fontSize: "11px", marginLeft: "6px" }}>
      <span
        style={{
          color: sortConfig.key === column && sortConfig.direction === "asc" ? "#fff" : "#bbb",
        }}
      >
        ▲
      </span>
      <span
        style={{
          marginLeft: "2px",
          color: sortConfig.key === column && sortConfig.direction === "desc" ? "#fff" : "#bbb",
        }}
      >
        ▼
      </span>
    </span>
  );

  // ฟังก์ชันสร้าง timestamp เวลานัดหมาย
  const getNextAppointTimestamp = (row) => {
    if (!row.Appoint_Date) return 0;
    let date = moment(row.Appoint_Date);

    let hh = "00";
    let mm = "00";

    if (row.Time_Start) {
      if (row.Time_Start.includes("T")) {
        hh = row.Time_Start.substring(11, 13);
        mm = row.Time_Start.substring(14, 16);
      } else {
        const p = row.Time_Start.split(":");
        hh = p[0] || "00";
        mm = p[1] || "00";
      }
    }

    return date.hour(Number(hh)).minute(Number(mm)).valueOf();
  };

  // สร้าง timestamp จาก Call_Date
  const getLastCallTimestamp = (row) => {
    if (!row.Call_Date) return 0;
    return moment.parseZone(row.Call_Date).valueOf();
  };

  // SORT
  const sortedData = React.useMemo(() => {
    let sorted = [...filteredData];

    if (!sortConfig.key) return sorted;

    sorted.sort((a, b) => {
      const key = sortConfig.key;

      // ถ้าเป็นคอลัมน์วันนัดหมาย → sort timestamp
      if (key === "next_appoint") {
        const x = getNextAppointTimestamp(a);
        const y = getNextAppointTimestamp(b);
        return sortConfig.direction === "asc" ? x - y : y - x;
      }

      // ถ้าเป็นคอลัมน์วันที่โทรล่าสุด → sort timestamp
      if (key === "last_call") {
        const x = getLastCallTimestamp(a);
        const y = getLastCallTimestamp(b);
        return sortConfig.direction === "asc" ? x - y : y - x;
      }

      // อื่นๆ sort string
      const x = (a[key] || "").toString().toLowerCase();
      const y = (b[key] || "").toString().toLowerCase();

      if (sortConfig.direction === "asc") return x > y ? 1 : -1;
      return x < y ? 1 : -1;
    });

    return sorted;
  }, [filteredData, sortConfig]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Pagination
  const totalItems = sortedData.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = sortedData.slice(startIndex, startIndex + itemsPerPage);

  // Logic: วันที่นัดหมายครั้งถัดไป
  const getNextAppoint = (row) => {
    const hasResult = !!row.Call_Result;
    const isAppoint = row.Call_Type === 2;

    if (!hasResult && !isAppoint) return "-";
    if (!hasResult && isAppoint)
      return `${formatThaiDate(row.Appoint_Date)} ${formatTime(row.Time_Start)} - ${formatTime(
        row.Time_End
      )}`;

    if (hasResult && !isAppoint) return "-";

    return `${formatThaiDate(row.Appoint_Date)} ${formatTime(row.Time_Start)} - ${formatTime(
      row.Time_End
    )}`;
  };

  const getLastCall = (row) => {
    if (!row.Call_Result) return "-";
    return formatThaiDateTimeFullCall(row.Call_Date);
  };

  return (
    <>
      <Sidebar />
      <Topbar breadcrumbs={breadcrumbs} />

      <div
        className="p-6"
        style={{
          marginLeft: "var(--sidebar-width)",
          marginTop: "56px",
          backgroundColor: "#f8f9fa",
          minHeight: "calc(100vh - 56px)",
        }}
      >
        <div className="card table-card border-0 rounded-0 shadow-sm bg-white p-3">
          {/* Header + Filter */}
          <div className="d-flex align-items-center gap-3 mb-3">
            <h3 className="fw-bold text-gray-700 fs-5 m-0">
              ประวัติการโทรหาลูกค้า {empName || "-"}
            </h3>

            <input
              type="text"
              placeholder="ค้นหาทุกช่อง..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="form-control"
              style={{ width: "260px", height: "38px" }}
            />

            <button
              className="btn btn-outline-secondary"
              style={{ width: "36px", height: "36px", padding: 0 }}
              onClick={() => {
                const prev = moment(selectedDate).subtract(1, "day").format("YYYY-MM-DD");
                setSelectedDate(prev);
                fetchData(prev);
              }}
            >
              ◀
            </button>

            <div style={{ width: "230px" }}>
              <DatePicker
                name="selectedDate"
                value={selectedDate}
                onChange={(d) => {
                  setSelectedDate(d);
                  fetchData(d);
                }}
                className="form-control text-center"
              />
            </div>

            <button
              className="btn btn-outline-secondary"
              style={{ width: "36px", height: "36px", padding: 0 }}
              onClick={() => {
                const next = moment(selectedDate).add(1, "day").format("YYYY-MM-DD");
                setSelectedDate(next);
                fetchData(next);
              }}
            >
              ▶
            </button>

            <button
              className="btn btn-outline-danger"
              style={{ height: "38px", padding: "0 16px" }}
              onClick={() => {
                const today = new Date().toISOString().split("T")[0];
                setSelectedDate(today);
                fetchData(today);
              }}
            >
              รีเซ็ตวันนี้
            </button>
          </div>

          {/* TABLE */}
          <style>{`
            table.table {
              border-collapse: collapse;
              width: 100%;
            }
            th {
              background-color: #E13F57 !important;
              color: white !important;
              cursor: pointer;
              user-select: none;
            }
            th, td {
              border: 1px solid #ccc !important;
              white-space: nowrap;
              padding: 6px 10px !important;
              vertical-align: middle !important;
            }
            tbody tr:nth-child(odd) { background: #fafafa; }
            tbody tr:nth-child(even) { background: #fff; }
            tbody tr:hover { background: #f5f7ff !important; }
            td.td-call-result { text-align: left !important; }
          `}</style>

          <div className="table-responsive" style={{ maxHeight: "70vh" }}>
            <table className="table table-bordered table-hover">
              <thead>
                <tr>
                  <th className="text-center" onClick={() => requestSort("Call_Id")}>
                    ลำดับ <SortIcon column="Call_Id" />
                  </th>

                  <th className="text-center" onClick={() => requestSort("AR_Name")}>
                    ชื่อลูกค้า <SortIcon column="AR_Name" />
                  </th>

                  <th className="text-center" onClick={() => requestSort("AR_Tel")}>
                    เบอร์ <SortIcon column="AR_Tel" />
                  </th>

                  <th className="text-center" onClick={() => requestSort("Contact_Name")}>
                    ชื่อผู้ติดต่อ <SortIcon column="Contact_Name" />
                  </th>

                  <th className="text-center" onClick={() => requestSort("Contact_Position")}>
                    ตำแหน่ง <SortIcon column="Contact_Position" />
                  </th>

                  <th className="text-center" onClick={() => requestSort("Call_Result")}>
                    ผลการโทร <SortIcon column="Call_Result" />
                  </th>

                  {/* วันที่นัดหมายครั้งถัดไป */}
                  <th className="text-center" onClick={() => requestSort("next_appoint")}>
                    วันที่นัดหมายครั้งถัดไป <SortIcon column="next_appoint" />
                  </th>

                  {/* วันที่โทรล่าสุด */}
                  <th className="text-center" onClick={() => requestSort("last_call")}>
                    วันที่โทรล่าสุด <SortIcon column="last_call" />
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4 text-muted">
                      กำลังโหลด…
                    </td>
                  </tr>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((row, idx) => (
                    <tr key={row.Call_Id || idx}>
                      <td className="text-center">{startIndex + idx + 1}</td>

                      <td className="text-start">
                        {`${row.AR_Name || ""} ${row.AR_LastName || ""}`.trim()}
                      </td>

                      <td>{formatPhone(row.AR_Tel)}</td>

                      <td>{row.Contact_Name || "-"}</td>

                      <td>{row.Contact_Position || "-"}</td>

                      <td className="td-call-result">{row.Call_Result || "ยังไม่มีผลการโทร"}</td>

                      <td className="text-center">{getNextAppoint(row)}</td>

                      <td className="text-center">{getLastCall(row)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center py-4 text-muted">
                      ไม่พบข้อมูล
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-2 border-top bg-white">
            <Pagination
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </div>
        </div>

        {/* Back button */}
        <div className="d-flex justify-content-end mt-3">
          <button
            onClick={() => {
              localStorage.setItem("saleReportDate", selectedDate);
              localStorage.setItem("saleReportSearch", searchText);
              localStorage.setItem("saleReportTab", "call");
              window.location.href = `/sale-report?date=${selectedDate}`;
            }}
            className="btn btn-dark btn-sm px-4 py-2"
            style={{ fontSize: "14px" }}
          >
            กลับหน้าค้นหา
          </button>
        </div>
      </div>

      {showCallModal && (
        <CallModal
          isOpen={showCallModal}
          initialData={selectedCall}
          onClose={() => setShowCallModal(false)}
          onSave={async (mode, payload, id) => {
            try {
              if (mode === "edit") {
                await api.put(`/update-sale-call/${id}`, payload);
              } else {
                await api.post(`/create-call`, payload);
              }

              setShowCallModal(false);
              fetchData(selectedDate);
            } catch {
              Swal.fire("ผิดพลาด", "บันทึกข้อมูลไม่สำเร็จ", "error");
            }
          }}
        />
      )}
    </>
  );
}
